import React from 'react';
import {
  PiPaperPlaneRight,
  PiArrowSquareOut,
  PiGearSix,
  PiPaperclip,
  PiFile,
  PiHouseLine,
} from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { FileInputButton } from '../../../whatsapp-media/file-input-button';
import type { WhatsAppActionBarProps } from 'src/modules/communications/types/whatsapp/action-bar.types';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import {
  IntegrationPermission,
  PropertyPermission,
} from 'src/modules/shared/types/permissions.type';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { PiUserCircle } from 'react-icons/pi';
import { ToggleButton } from 'src/modules/shared/components/atoms/buttons/toggle-button';
import { QuickTemplateIcon } from 'src/modules/shared/components/atoms/icons/custom-icons/quick-template-icon';
import { WhatsAppTemplateIcon } from 'src/modules/shared/components/atoms/icons/custom-icons/whatsapp-template-icon';

const WHATSAPP_TEMPLATES_MANAGEMENT_URL =
  'https://business.facebook.com/latest/whatsapp_manager/message_templates';

export const WhatsAppActionBar: React.FC<WhatsAppActionBarProps> = ({
  children,
  inputActions,
  onSharePropertyClick,
  onFileSelect,
  onFileValidationError,
  onTemplateClick,
  onSendClick,
  onQuickTemplateClick,
  onInsertSlash,
  isLoading,
  isSendingMessage,
  isUploading,
  uploadStep,
  sessionActive,
  channelSession,
  isTestConversation = false,
  showReplyAsContactToggle = false,
  isReplyingAsContact = false,
  onReplyAsContactChange,
}) => {
  const { t } = useI18n();
  const { hasPermission: canManageWhatsApp } = useHasPermissions(
    IntegrationPermission.Update
  );
  const { hasPermission: canReadAllProperties } = useHasPermissions(
    PropertyPermission.Read
  );
  const { hasPermission: canReadScopedProperties } = useHasPermissions(
    PropertyPermission.ReadScoped
  );
  const canUseTestTransport = isTestConversation;
  const actionsDisabled = isLoading || isSendingMessage || isReplyingAsContact;

  const isSendDisabled =
    isSendingMessage ||
    isLoading ||
    (isReplyingAsContact
      ? false
      : isUploading ||
        uploadStep === 'uploading' ||
        uploadStep === 'error' ||
        (!canUseTestTransport && (!channelSession || !sessionActive)));

  const isFileInputDisabled =
    isReplyingAsContact ||
    isLoading ||
    isSendingMessage ||
    isUploading ||
    (!canUseTestTransport && !sessionActive);

  const handleInsertSlash = () => {
    if (!canUseTestTransport && !sessionActive) return;

    onInsertSlash();
  };

  return (
    <div className="flex min-w-0 items-end gap-1 p-1">
      <div className="flex shrink-0 items-center">
        {showReplyAsContactToggle && onReplyAsContactChange && (
          <ToggleButton
            aria-label="Mensaje entrante"
            title="Simula un mensaje entrante del contacto"
            pressed={isReplyingAsContact}
            onPressedChange={onReplyAsContactChange}
            size="md"
            variant="primary"
            appearance="ghost"
            shape="round"
            isIconOnly
          >
            <PiUserCircle className="h-4 w-4" />
          </ToggleButton>
        )}

        <FileInputButton
          onFileSelect={onFileSelect}
          onValidationError={onFileValidationError}
          disabled={isFileInputDisabled}
          renderTrigger={(openFilePicker) => (
            <MenuDropdown
              placement="top-start"
              size="md"
              disabled={actionsDisabled}
              mobileTitle={t('composer.attachments.title')}
              handlerComponent={
                <Button
                  size="md"
                  appearance="ghost"
                  variant="primary"
                  shape="round"
                  isIconOnly
                  aria-label={t('composer.attachments.title')}
                  disabled={actionsDisabled}
                >
                  <PiPaperclip className="h-5 w-5" />
                </Button>
              }
              items={[
                {
                  content: t('composer.attachments.file'),
                  icon: <PiFile className="h-5 w-5" />,
                  onClick: openFilePicker,
                  disabled: isFileInputDisabled,
                },
                ...(canReadAllProperties || canReadScopedProperties
                  ? [
                      {
                        content: t('propertyLinks.share'),
                        icon: <PiHouseLine className="h-5 w-5" />,
                        onClick: onSharePropertyClick,
                        disabled: isFileInputDisabled,
                      },
                    ]
                  : []),
                {
                  content: t('composer.attachments.quickTemplate'),
                  icon: <QuickTemplateIcon className="h-5 w-5" />,
                  onClick: handleInsertSlash,
                  disabled: !canUseTestTransport && !sessionActive,
                },
                {
                  content: t('composer.attachments.whatsappTemplate'),
                  icon: <WhatsAppTemplateIcon className="h-5 w-5" />,
                  onClick: onTemplateClick,
                },
                {
                  content: t('composer.attachments.manageQuickTemplates'),
                  icon: <PiGearSix className="h-5 w-5" />,
                  onClick: onQuickTemplateClick,
                },
                ...(canManageWhatsApp
                  ? [
                      {
                        content: t(
                          'composer.attachments.manageWhatsAppTemplates'
                        ),
                        icon: <PiArrowSquareOut className="h-5 w-5" />,
                        onClick: () =>
                          window.open(
                            WHATSAPP_TEMPLATES_MANAGEMENT_URL,
                            '_blank',
                            'noopener,noreferrer'
                          ),
                      },
                    ]
                  : []),
              ]}
            />
          )}
        />
        {inputActions}
      </div>

      {children}

      <div className="ml-auto flex-shrink-0">
        <Tooltip
          content={
            isReplyingAsContact
              ? 'Simula un mensaje entrante del contacto'
              : 'Envía el mensaje'
          }
          size="sm"
          placement="top"
        >
          <Button
            aria-label={t('shared.actions.send')}
            onClick={onSendClick}
            disabled={isSendDisabled}
            loading={isSendingMessage}
            size="md"
            appearance="soft"
            variant="whatsapp"
            shape="round"
            isIconOnly
          >
            <PiPaperPlaneRight className="h-5 w-5" />
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};
